/* Created by Interactive 63 - https://interactive63.com */
/**
 * Toast notifications: short messages stacked at the bottom centre of the window. Info and
 * warning toasts dismiss themselves; errors stay until dismissed. The container is an
 * aria-live region so screen readers announce new messages.
 */
import { h } from './dom';

export type ToastKind = 'info' | 'warning' | 'error';

const AUTO_DISMISS_MS = 5000;

let container: HTMLElement | null = null;

/** Returns the shared toast container, creating it on first use. */
function getContainer(): HTMLElement {
  if (container?.isConnected) return container;
  container = h('div', { class: 'toasts', 'aria-live': 'polite', role: 'status' });
  document.body.append(container);
  return container;
}

/** Shows a toast and returns a function that dismisses it early. */
export function showToast(message: string, kind: ToastKind = 'info'): () => void {
  const toast = h('div', { class: ['toast', `toast-${kind}`] });
  const dismiss = (): void => {
    toast.remove();
  };
  toast.append(
    h('span', { class: 'toast-message' }, message),
    h(
      'button',
      { type: 'button', class: 'toast-close', 'aria-label': 'Dismiss', onClick: dismiss },
      '×',
    ),
  );
  getContainer().append(toast);
  if (kind !== 'error') setTimeout(dismiss, AUTO_DISMISS_MS);
  return dismiss;
}

export const toast = {
  info: (message: string): (() => void) => showToast(message, 'info'),
  warning: (message: string): (() => void) => showToast(message, 'warning'),
  error: (message: string): (() => void) => showToast(message, 'error'),
};
